import { Select, Text } from "@instructure/ui";
import { useEffect, useState } from "react";

type Option = { id: string; label: string };

type Options<T extends Option = Option> = Record<string, T[]>;

interface GroupSelectProps<T extends Option> {
  id: string;
  label: string;
  options: Options<T>;
  selectHandler: (option: T) => void;
  selectedItemId: string;
  disabled?: boolean;
  size?: "small" | "medium" | "large";
  visibleOptionsCount?: number;
}

const GroupSelect = <T extends Option>({
  id,
  label,
  options,
  selectHandler,
  selectedItemId,
  disabled,
  size,
  visibleOptionsCount,
}: GroupSelectProps<T>) => {
  const [inputValue, setInputValue] = useState<string>("");
  const [filteredOptions, setFilteredOptions] = useState<Options>(options);
  const [highlightedOptionId, setHighlightedOptionId] = useState<
    string | undefined
  >("");
  const [isShowingOptions, setIsShowingOptions] = useState<boolean>(false);

  useEffect(() => {
    const option = getOptionById(selectedItemId);
    setInputValue(option ? option.label : "");
  }, [selectedItemId]);

  function getOptionById(queryId: string | undefined): T | undefined {
    if (!queryId) return undefined;
    return Object.values(options)
      .flat()
      .find(({ id }) => id === queryId);
  }

  function getOptionsByValue(value: string): Options {
    return Object.fromEntries(
      Object.entries(options)
        .map(([group, opts]): [string, Option[]] => [
          group,
          opts.filter((o) =>
            o.label.toLowerCase().startsWith(value.toLowerCase())
          ),
        ])
        .filter(([, opts]) => opts.length > 0)
    );
  }

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const value = event.target.value;
    const newOptions = getOptionsByValue(value);
    setInputValue(value);
    setFilteredOptions(newOptions);
    setHighlightedOptionId(Object.values(newOptions).flat()[0]?.id ?? "");
    setIsShowingOptions(true);
  }

  function handleShowOptions() {
    setIsShowingOptions(true);
  }
  function handleHideOptions() {
    setIsShowingOptions(false);
  }

  function handleHighlightOption(
    event: React.SyntheticEvent,
    { id }: { id?: string; direction?: 1 | -1 }
  ) {
    const option = getOptionById(id);
    if (!option) return;
    setHighlightedOptionId(id);
    setInputValue((prevValue) =>
      event.type === "keydown" ? option.label : prevValue
    );
  }

  function handleSelectOption(
    _e: React.SyntheticEvent,
    { id }: { id?: string }
  ) {
    const option = getOptionById(id);
    if (!option) return;
    setInputValue(option.label);
    setIsShowingOptions(false);
    selectHandler(option);
    setFilteredOptions(getOptionsByValue(""));
  }

  const Group = () => {
    return Object.entries(filteredOptions).map(([text, opts]) => {
      return (
        <Select.Group key={text} renderLabel={text}>
          {opts.map((option: Option) => (
            <Select.Option
              key={option.id}
              id={option.id}
              isHighlighted={option.id === highlightedOptionId}
              isSelected={option.id === selectedItemId}
            >
              {option.label}
            </Select.Option>
          ))}
        </Select.Group>
      );
    });
  };

  return (
    <Select
      id={id}
      renderLabel={<Text size={size}>{label}</Text>}
      inputValue={inputValue}
      size={size || "medium"}
      onInputChange={handleInputChange}
      isShowingOptions={isShowingOptions}
      onRequestShowOptions={handleShowOptions}
      onRequestHideOptions={handleHideOptions}
      onRequestHighlightOption={handleHighlightOption}
      onRequestSelectOption={handleSelectOption}
      interaction={disabled ? "disabled" : "enabled"}
      visibleOptionsCount={visibleOptionsCount}
    >
      {Group()}
    </Select>
  );
};

export default GroupSelect;
